import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { getCertificaten, createCertificaat, updateCertificaat, deleteCertificaat, uploadCertificaatPdf } from "../Reducers/actions";
import Modal from "../../../Components/Modal/Modal";
import RichTextEditor from "react-rte";

const CertificatenPage = () => {
  const dispatch = useDispatch();
  const [certificaten, setCertificaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCertificaat, setEditingCertificaat] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    eisen: "",
    foto_required: false,
    pdf_template: ""
  });
  const [eisenValue, setEisenValue] = useState(RichTextEditor.createEmptyValue());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCertificaten();
  }, []);

  const loadCertificaten = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getCertificaten());
      setCertificaten(response.results || []);
    } catch (error) {
      console.error("Error loading certificaten:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (certificaat = null) => {
    if (certificaat) {
      setEditingCertificaat(certificaat);
      setFormData({
        name: certificaat.name,
        eisen: certificaat.eisen || "",
        foto_required: certificaat.foto_required || false,
        pdf_template: certificaat.pdf_template || ""
      });
      setEisenValue(
        certificaat.eisen
          ? RichTextEditor.createValueFromString(certificaat.eisen, "html")
          : RichTextEditor.createEmptyValue()
      );
    } else {
      setEditingCertificaat(null);
      setFormData({
        name: "",
        eisen: "",
        foto_required: false,
        pdf_template: ""
      });
      setEisenValue(RichTextEditor.createEmptyValue());
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCertificaat(null);
    setFormData({
      name: "",
      eisen: "",
      foto_required: false,
      pdf_template: ""
    });
    setEisenValue(RichTextEditor.createEmptyValue());
  };

  const handleEisenChange = (value) => {
    setEisenValue(value);
    setFormData({ ...formData, eisen: value.toString("html") });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Alleen PDF bestanden zijn toegestaan");
      return;
    }

    setUploading(true);
    try {
      const response = await dispatch(uploadCertificaatPdf(file));
      if (response.success) {
        setFormData({ ...formData, pdf_template: response.result.url });
      } else {
        alert("Fout bij uploaden PDF");
      }
    } catch (error) {
      console.error("Error uploading PDF:", error);
      alert("Fout bij uploaden PDF");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemovePdf = () => {
    setFormData({ ...formData, pdf_template: "" });
  };

  const handleSave = async () => {
    if (!formData.name) {
      alert("Vul de naam in");
      return;
    }

    setSaving(true);
    try {
      if (editingCertificaat) {
        await dispatch(updateCertificaat(editingCertificaat._id, formData));
      } else {
        await dispatch(createCertificaat(formData));
      }
      handleCloseModal();
      loadCertificaten();
    } catch (error) {
      console.error("Error saving certificaat:", error);
      alert("Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit certificaat wilt verwijderen?")) {
      return;
    }

    try {
      await dispatch(deleteCertificaat(id));
      loadCertificaten();
    } catch (error) {
      console.error("Error deleting certificaat:", error);
      alert("Fout bij verwijderen");
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3>Certificaten</h3>
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                <i className="fas fa-plus mr-2"></i>
                Nieuw Certificaat
              </button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Laden...</span>
                  </div>
                </div>
              ) : certificaten.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-certificate fa-3x text-muted mb-3"></i>
                  <p className="text-muted">Nog geen certificaten aangemaakt</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleOpenModal()}
                  >
                    Maak je eerste certificaat
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Naam</th>
                        <th style={{width: '100px'}}>Foto</th>
                        <th style={{width: '150px'}}>PDF Template</th>
                        <th style={{width: '150px'}}>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificaten.map((certificaat) => (
                        <tr key={certificaat._id}>
                          <td>
                            <strong>{certificaat.name}</strong>
                          </td>
                          <td>
                            {certificaat.foto_required ? (
                              <span className="badge badge-success">
                                <i className="fas fa-check mr-1"></i>
                                Ja
                              </span>
                            ) : (
                              <span className="badge badge-secondary">
                                Nee
                              </span>
                            )}
                          </td>
                          <td>
                            {certificaat.pdf_template ? (
                              <a
                                href={certificaat.pdf_template}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-info"
                              >
                                <i className="fas fa-file-pdf mr-1"></i>
                                Bekijk
                              </a>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary mr-2"
                              onClick={() => handleOpenModal(certificaat)}
                              title="Bewerken"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(certificaat._id)}
                              title="Verwijderen"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal voor toevoegen/bewerken */}
      <Modal
        visible={showModal}
        onClose={handleCloseModal}
        width={800}
        className="certificaat-modal"
      >
        <div className="p-4">
          <h4 className="mb-4">
            {editingCertificaat ? "Certificaat Bewerken" : "Nieuw Certificaat"}
          </h4>

          <div className="form-group">
            <label><strong>Naam *</strong></label>
            <input
              type="text"
              className="form-control"
              placeholder="Bijv. NEN 3140 Certificaat"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label><strong>Eisen</strong></label>
            <RichTextEditor
              className="wysiwyg-editor"
              value={eisenValue}
              onChange={handleEisenChange}
              placeholder="Beschrijf hier de eisen voor dit certificaat..."
            />
          </div>

          <div className="form-group">
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="fotoRequired"
                checked={formData.foto_required}
                onChange={(e) => setFormData({ ...formData, foto_required: e.target.checked })}
              />
              <label className="custom-control-label" htmlFor="fotoRequired">
                <strong>Foto verplicht</strong>
                <br />
                <small className="text-muted">Vereist een foto bij dit certificaat</small>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label><strong>PDF Template</strong></label>
            {formData.pdf_template ? (
              <div className="d-flex align-items-center">
                <a
                  href={formData.pdf_template}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-info mr-2"
                >
                  <i className="fas fa-file-pdf mr-2"></i>
                  Bekijk huidige PDF
                </a>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleRemovePdf}
                >
                  <i className="fas fa-times mr-1"></i>
                  Verwijderen
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="d-none"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2"></span>
                      Uploaden...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload mr-2"></i>
                      Upload PDF Template
                    </>
                  )}
                </button>
                <small className="text-muted d-block mt-1">
                  Maximaal 20MB, alleen PDF bestanden
                </small>
              </div>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              className="btn btn-secondary mr-2"
              onClick={handleCloseModal}
            >
              Annuleren
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Opslaan...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Opslaan
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CertificatenPage;
